import { httpClient } from './http-client';
import type { ApiResponse } from './api.types';
import type { NotaDeVenta } from '@/modelos/documentosComerciales/NotaDeVenta';
import type { DocumentoComercialItem } from '@/modelos/documentosComerciales/DocumentoComercialItem';
import type { DocumentoRelacionadoComercialDto } from '@/modelos/documentosComerciales/DocumentoRelacionadoComercial';

export interface NotaDeVentaItemDto {
  id: string;
  empresaId?: string;
  establecimientoId?: string;
  establecimientoCodigo?: string;
  establecimientoNombre?: string;
  productoId?: string;
  productoCodigo?: string;
  productoNombre: string;
  codigoSunat?: string;
  descripcion?: string;
  unidadMedidaId?: string;
  unidadMedidaNombre?: string;
  unidadMedidaCodigoSunat?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  cantidad: number;
  precioUnitario: number;
  valorVentaUnitario: number;
  precioVentaUnitario: number;
  descuento: number;
  descuentoFactor: number;
  tipoDescuentoFormato?: string;
  valorVentaNeto: number;
  precioVentaNeto: number;
  valorVentaDescontado: number;
  precioVentaDescontado: number;
  subtotal: number;
  igvPorcentaje: number;
  igv: number;
  igvTotal: number;
  total: number;
  orden: number;
  esDetraccion?: boolean;
  detraccionCodigo?: string;
  detraccionPorcentaje?: number;
  usuarioId?: string;
  usuarioNombre?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface NotaDeVentaListadoDto {
  id: string;
  empresaId?: string;
  establecimientoId?: string;
  establecimientoCodigo?: string;
  establecimientoNombre?: string;
  serie?: string;
  correlativo?: string;
  numero?: string;
  fechaEmision?: string;
  clienteId?: string;
  clienteNumeroDocumento?: string;
  clienteTipoDocumentoCodigo?: string;
  clienteNombres?: string;
  clienteCorreo?: string;
  clienteDireccion?: string;
  vendedorId?: string;
  vendedorNombre?: string;
  monedaCodigo?: string;
  monedaNombre?: string;
  tipoCambio?: number;
  formaPagoId?: string;
  formaPagoNombre?: string;
  cotizacionOrigenId?: string;
  ordenVentaOrigenId?: string;
  metodoEnvio?: string;
  fechaEnvioPrevisto?: string;
  fechaVencimiento?: string;
  ordenCompra?: string;
  centroCosto?: string;
  numeroGuia?: string;
  comprobanteId?: string;
  comprobanteNumero?: string;
  subtotal?: number;
  montoImponible?: number;
  descuentoTotal?: number;
  valorDescuentoGlobal?: number;
  tipoDescuentoGlobal?: string;
  descuentoItems?: number;
  exonerado?: number;
  inafecto?: number;
  exportacion?: number;
  igv?: number;
  total?: number;
  estado?: string;
  tieneNotaSalidaActiva?: boolean;
  motivoAnulacion?: string;
  observacion?: string;
  observacionInterno?: string;
  usuarioId?: string;
  usuarioNombre?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface NotaVentaHistorialDto {
  id: string;
  estado: string;
  descripcion?: string;
  usuarioNombre?: string;
  createdAt: string;
}

export interface NotaDeVentaDto extends NotaDeVentaListadoDto {
  items: NotaDeVentaItemDto[];
  historialDocumentos?: NotaVentaHistorialDto[];
}

export interface NotaDeVentaItemInputDto {
  id?: string;
  productoId?: string;
  productoCodigo?: string;
  productoNombre: string;
  codigoSunat?: string;
  descripcion?: string;
  unidadMedidaId?: string;
  unidadMedidaNombre?: string;
  unidadMedidaCodigoSunat?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  cantidad: number;
  precioUnitario: number;
  valorVentaUnitario?: number;
  precioVentaUnitario?: number;
  descuento: number;
  descuentoFactor?: number;
  tipoDescuentoFormato?: string;
  valorVentaNeto?: number;
  precioVentaNeto?: number;
  valorVentaDescontado?: number;
  precioVentaDescontado?: number;
  subtotal: number;
  igvPorcentaje: number;
  igv: number;
  igvTotal?: number;
  total: number;
  orden: number;
}

export interface NotaDeVentaInputDto {
  establecimientoId: string;
  serie?: string;
  fechaEmision: string;
  clienteId?: string;
  clienteNumeroDocumento?: string;
  clienteTipoDocumentoCodigo?: string;
  clienteNombres?: string;
  clienteCorreo?: string;
  clienteDireccion?: string;
  vendedorId?: string;
  monedaCodigo: string;
  monedaNombre?: string;
  tipoCambio: number;
  formaPagoId?: string;
  cotizacionOrigenId?: string;
  ordenVentaOrigenId?: string;
  metodoEnvio?: string;
  fechaEnvioPrevisto?: string;
  fechaVencimiento?: string;
  ordenCompra?: string;
  centroCosto?: string;
  numeroGuia?: string;
  subtotal: number;
  montoImponible?: number;
  descuentoTotal: number;
  valorDescuentoGlobal?: number;
  tipoDescuentoGlobal?: string;
  descuentoItems?: number;
  exonerado?: number;
  inafecto?: number;
  exportacion?: number;
  igv: number;
  total: number;
  observacion?: string;
  observacionInterno?: string;
  items: NotaDeVentaItemInputDto[];
}

function dtoAItem(dto: NotaDeVentaItemDto): DocumentoComercialItem {
  return {
    id: dto.id,
    empresaId: dto.empresaId,
    establecimientoId: dto.establecimientoId,
    establecimientoCodigo: dto.establecimientoCodigo,
    establecimientoNombre: dto.establecimientoNombre,
    productoId: dto.productoId,
    productoCodigo: dto.productoCodigo,
    productoNombre: dto.productoNombre,
    codigoSunat: dto.codigoSunat,
    descripcion: dto.descripcion,
    unidadMedidaId: dto.unidadMedidaId,
    unidadMedidaNombre: dto.unidadMedidaNombre,
    unidadMedidaCodigoSunat: dto.unidadMedidaCodigoSunat,
    afectacionTributariaId: dto.afectacionTributariaId,
    afectacionTributariaCodigo: dto.afectacionTributariaCodigo,
    afectacionTributariaCodigoTributo: dto.afectacionTributariaCodigoTributo,
    afectacionTributariaImpuesto: dto.afectacionTributariaImpuesto,
    afectacionTributariaNombre: dto.afectacionTributariaNombre,
    categoriaId: dto.categoriaId,
    categoriaNombre: dto.categoriaNombre,
    cantidad: dto.cantidad,
    precioUnitario: dto.precioUnitario,
    valorVentaUnitario: dto.valorVentaUnitario ?? 0,
    precioVentaUnitario: dto.precioVentaUnitario ?? 0,
    descuento: dto.descuento,
    descuentoFactor: dto.descuentoFactor ?? 0,
    tipoDescuentoFormato: dto.tipoDescuentoFormato,
    valorVentaNeto: dto.valorVentaNeto ?? 0,
    precioVentaNeto: dto.precioVentaNeto ?? 0,
    valorVentaDescontado: dto.valorVentaDescontado ?? 0,
    precioVentaDescontado: dto.precioVentaDescontado ?? 0,
    subtotal: dto.subtotal,
    igvPorcentaje: dto.igvPorcentaje,
    igv: dto.igv,
    igvTotal: dto.igvTotal ?? dto.igv,
    total: dto.total,
    orden: dto.orden,
    esDetraccion: dto.esDetraccion,
    detraccionCodigo: dto.detraccionCodigo,
    detraccionPorcentaje: dto.detraccionPorcentaje,
    usuarioId: dto.usuarioId,
    usuarioNombre: dto.usuarioNombre,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export function dtoANotaDeVenta(dto: NotaDeVentaListadoDto): NotaDeVenta {
  return {
    id: dto.id,
    empresaId: dto.empresaId ?? '',
    establecimientoId: dto.establecimientoId ?? '',
    establecimientoCodigo: dto.establecimientoCodigo,
    establecimientoNombre: dto.establecimientoNombre,
    serie: dto.serie ?? '',
    correlativo: dto.correlativo,
    numero: dto.numero,
    fechaEmision: dto.fechaEmision ?? '',
    clienteId: dto.clienteId,
    clienteNumeroDocumento: dto.clienteNumeroDocumento,
    clienteTipoDocumentoCodigo: dto.clienteTipoDocumentoCodigo,
    clienteNombres: dto.clienteNombres,
    clienteCorreo: dto.clienteCorreo,
    clienteDireccion: dto.clienteDireccion,
    vendedorId: dto.vendedorId,
    vendedorNombre: dto.vendedorNombre,
    monedaCodigo: dto.monedaCodigo ?? 'PEN',
    monedaNombre: dto.monedaNombre,
    tipoCambio: dto.tipoCambio ?? 1,
    formaPagoId: dto.formaPagoId,
    formaPagoNombre: dto.formaPagoNombre,
    cotizacionOrigenId: dto.cotizacionOrigenId,
    ordenVentaOrigenId: dto.ordenVentaOrigenId,
    metodoEnvio: dto.metodoEnvio,
    fechaEnvioPrevisto: dto.fechaEnvioPrevisto,
    fechaVencimiento: dto.fechaVencimiento,
    ordenCompra: dto.ordenCompra,
    centroCosto: dto.centroCosto,
    numeroGuia: dto.numeroGuia,
    comprobanteId: dto.comprobanteId,
    comprobanteNumero: dto.comprobanteNumero,
    subtotal: dto.subtotal ?? 0,
    montoImponible: dto.montoImponible ?? 0,
    descuentoTotal: dto.descuentoTotal ?? 0,
    valorDescuentoGlobal: dto.valorDescuentoGlobal ?? 0,
    tipoDescuentoGlobal: dto.tipoDescuentoGlobal,
    descuentoItems: dto.descuentoItems ?? 0,
    exonerado: dto.exonerado ?? 0,
    inafecto: dto.inafecto ?? 0,
    exportacion: dto.exportacion ?? 0,
    igv: dto.igv ?? 0,
    total: dto.total ?? 0,
    estado: (dto.estado as NotaDeVenta['estado']) ?? 'borrador',
    tieneNotaSalidaActiva: dto.tieneNotaSalidaActiva ?? false,
    motivoAnulacion: dto.motivoAnulacion,
    observacion: dto.observacion,
    observacionInterno: dto.observacionInterno,
    usuarioId: dto.usuarioId,
    usuarioNombre: dto.usuarioNombre,
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    items: (dto as NotaDeVentaDto).items?.map(dtoAItem),
    historial: (dto as NotaDeVentaDto).historialDocumentos,
  };
}

export const notasVentaService = {
  getAll: (params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<NotaDeVentaListadoDto[]>> =>
    httpClient.get('/notas-venta', params),

  getById: async (id: string): Promise<ApiResponse<NotaDeVenta>> => {
    const res = await httpClient.get<NotaDeVentaDto>(`/notas-venta/${id}`);
    if (!res.ok || !res.data) return { ...res, data: undefined as unknown as NotaDeVenta };
    return { ...res, data: dtoANotaDeVenta(res.data) };
  },

  create: (data: NotaDeVentaInputDto): Promise<ApiResponse<NotaDeVentaListadoDto>> =>
    httpClient.post('/notas-venta', data),

  update: (id: string, data: NotaDeVentaInputDto): Promise<ApiResponse<NotaDeVentaListadoDto>> =>
    httpClient.put(`/notas-venta/${id}`, data),

  generar: (id: string, serie: string): Promise<ApiResponse<NotaDeVentaListadoDto>> =>
    httpClient.patch(`/notas-venta/${id}/generar`, { serie }),

  getRelacionados: (id: string): Promise<ApiResponse<DocumentoRelacionadoComercialDto[]>> =>
    httpClient.get(`/notas-venta/${id}/relacionados`),

  anular: (id: string, motivo: string): Promise<ApiResponse<NotaDeVentaListadoDto>> =>
    httpClient.patch(`/notas-venta/${id}/anular`, { motivo }),

  eliminar: (id: string): Promise<ApiResponse<void>> =>
    httpClient.delete(`/notas-venta/${id}`),

  downloadPdf: (id: string): Promise<Blob> =>
    httpClient.obtenerBlob(`/notas-venta/${id}/pdf`),

  enviarCorreo: (id: string, email: string, verUrl: string): Promise<ApiResponse<void>> =>
    httpClient.post(`/notas-venta/${id}/enviar-correo`, { email, verUrl }),

  downloadExcel: (params?: {
    empresaId?: string;
    establecimientoId?: string;
    search?: string;
    estado?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    vendedor?: string;
    moneda?: string;
    totalMin?: number;
    totalMax?: number;
    ids?: string[];
  }): Promise<Blob> => {
    const { ids, ...rest } = params ?? {};
    const query: Record<string, string | number | boolean | undefined> = rest as Record<string, string | number | boolean | undefined>;
    if (ids && ids.length > 0) query.ids = ids.join(',');
    return httpClient.obtenerBlob('/reportes/notas-venta/excel', query);
  },
};
